/* eslint-disable react/no-array-index-key */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAccount } from 'providers/AccountProvider';
import ArrowIcon from 'assets/icons/button_arrow_icon.svg';
import './styles.scss';
import { axios } from 'startup';
import config from 'config';
import { Spin, notification } from 'antd';
import { useNavigate } from 'react-router-dom';
import { Dropdown, SearchInput, useForm } from 'linkwithus';
import { IDropdownItemDTO } from 'DTOModels/dropdownItemDTO';
import { removeCookie, SESSION_COOKIE_KEY } from 'services/cookies';
import { dropdownItems, pagination } from 'utils/pagination';
import ConfirmationModal from 'components/ConfirmationModal';
import { IAccommodationListItemDTO } from 'DTOModels/accommodationDTO';
import AccommodationModal from 'components/AccommodationModal';
import { AccommodationModalMode } from 'components/AccommodationModal/interface';
import { IDeleteUserForm, IOrderFields } from './interface';
import AccommodationFilterTableItem from './components/AccommodationFilterTableItem';
import { IOrderType } from '../OrderButton/interface';
import OrderButton from '../OrderButton';

function AccommodationFilterTable() {
    const { t } = useTranslation();
    const [currentMode, setCurrentMode] = useState<AccommodationModalMode>();
    const [confirmationModalVisibility, setConfirmationModalVisibility] =
        useState(false);
    const [accommodationModalVisibility, setAccommodationModalVisibility] =
        useState(false);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { setAccount } = useAccount();
    const [totalItems, setTotalItems] = useState<IAccommodationListItemDTO[]>(
        [],
    );
    const [itemsPagination, setItemsPagination] = useState<
        IAccommodationListItemDTO[][]
    >([]);
    const [itemsPaginationDropdown, setItemsPaginationDropdown] = useState<
        IDropdownItemDTO[]
    >([]);
    const [currentPageNumber, setCurrentPageNumber] = useState(1);
    const [totalNumberOfItems, setTotalNumberOfItems] = useState(0);
    const [totalPageNumber, setTotalPageNumber] = useState(5);
    const [intermediarySearchFilter, setIntermediarySearchFilter] = useState<
        string | undefined
    >();
    const [searchFilter, setSearchFilter] = useState<string | undefined>();
    const [currentOrderType, setCurrentOrderType] = useState(IOrderFields.Name);
    const [currentOrder, setCurrentOrder] = useState(IOrderType.Descending);
    const [selectedItem, setSelectedItem] =
        useState<IAccommodationListItemDTO>();

    const changeOrderType = useCallback((order: IOrderFields) => {
        setCurrentOrderType(order);
        setCurrentOrder(IOrderType.Descending);
    }, []);

    const logout = useCallback(() => {
        // timeout just to add a bit of delay
        setTimeout(() => {
            removeCookie(SESSION_COOKIE_KEY);
            if (setAccount) setAccount(undefined);
            navigate('/login');
        }, 250);
        notification.error({
            message: t('general.ERROR'),
            description: t('general.SUSPICIOUS_ACTIVITY'),
        });
    }, []);

    const changeOrder = useCallback(() => {
        if (currentOrder === IOrderType.Ascending)
            setCurrentOrder(IOrderType.Descending);
        else {
            setCurrentOrder(IOrderType.Ascending);
        }
    }, [currentOrder]);

    const fetchList = () => {
        setLoading(true);
        // currentOrderType, currentOrder
        const currentDate = new Date();
        const orderTypeString = currentOrder
            ? `&orderType=${currentOrder}`
            : '';
        const orderFieldString = currentOrderType
            ? `&orderField=${currentOrderType}`
            : '';
        const searchString =
            searchFilter && searchFilter !== ''
                ? `&search=${searchFilter}`
                : '';
        axios
            .get(
                `${
                    config.API_URL
                }/accommodations?limit=${1}${searchString}${orderTypeString}${orderFieldString}&date=${currentDate}`,
            )
            .then(response => {
                setTotalItems(
                    response.data.Accommodations.map(
                        (item: IAccommodationListItemDTO) => ({
                            ...item,
                        }),
                    ),
                );
                setTotalNumberOfItems(response.data.Total);
                setCurrentPageNumber(1);
                setSelectedItem(undefined);
            })
            .catch(() => {
                // force logout
                logout();
            });
    };

    const { form, updateForm, submit } = useForm<IDeleteUserForm>({
        url: `/accommodations/${selectedItem ? selectedItem.Id : ''}`,
        type: 'PATCH',
        onSuccess: () => {
            setConfirmationModalVisibility(false);
            notification.success({
                message: t('general.SUCCESS'),
                description: t('general.ACCOMMODATION_DELETE_SUCCESS'),
            });
            fetchList();
        },
        onError: (data: any, error: any) => {
            setConfirmationModalVisibility(false);
            const errorCode = Number(error.errorCode) || 0;
            notification.error({
                message: t(`general.ERROR`),
                description: t(`errors.${errorCode}`),
            });
        },
    });

    useEffect(() => {
        fetchList();
    }, [searchFilter, currentOrder, currentOrderType]);

    useEffect(() => {
        setItemsPagination(pagination(totalItems, totalPageNumber));
        setLoading(false);
    }, [totalItems, totalPageNumber]);

    useEffect(() => {
        setItemsPaginationDropdown(
            dropdownItems(
                totalNumberOfItems && totalNumberOfItems > 5
                    ? totalNumberOfItems
                    : 5,
            ),
        );
    }, [totalNumberOfItems]);

    // throttle set of input Search (300 miliseconds)
    useEffect(() => {
        const interval = setInterval(() => {
            if (intermediarySearchFilter === '') {
                setSearchFilter(undefined);
                return;
            }
            setSearchFilter(intermediarySearchFilter);
        }, 300);
        return () => {
            clearInterval(interval);
        };
    }, [intermediarySearchFilter]);

    useEffect(() => {
        if (selectedItem) {
            updateForm('accommodationId', selectedItem.Id);
        }
    }, [selectedItem]);

    return (
        <div className='accommodation-filter-table'>
            <AccommodationModal
                modalVisibility={accommodationModalVisibility}
                close={() => {
                    setAccommodationModalVisibility(false);
                }}
                updateList={fetchList}
                mode={currentMode}
                item={selectedItem}
            />
            <ConfirmationModal
                modalVisibility={confirmationModalVisibility}
                close={() => {
                    setConfirmationModalVisibility(false);
                }}
                confirmation={() => {
                    // do request to soft-delete dish
                    if (!form.accommodationId) {
                        return;
                    }
                    submit();
                }}
                description={t(
                    'contentSettingsPage.CONFIRM_DELETE_ACCOMMODATION',
                )}
            />
            <div className='accommodation-filter-table__header'>
                <div className='accommodation-filter-table__header__left-column'>
                    <SearchInput
                        onChange={(e: string) => {
                            setIntermediarySearchFilter(e);
                        }}
                        value={intermediarySearchFilter}
                        placeholder={t('general.SEARCH')}
                        className='accommodation-filter-table__header__left-column__search'
                    />
                </div>
                <div className='accommodation-filter-table__header__right-column'>
                    <button
                        type='button'
                        onClick={() => {
                            setCurrentMode(AccommodationModalMode.Create);
                            setAccommodationModalVisibility(true);
                        }}
                    >
                        {t('contentSettingsPage.ADD_ACCOMMODATION')}
                    </button>
                </div>
            </div>
            <div className='accommodation-filter-table__order'>
                <div className='accommodation-filter-table__order__field'>
                    <OrderButton
                        name={t('contentSettingsPage.IMAGE')}
                        active={currentOrderType === IOrderFields.Image}
                    />
                </div>
                <div className='accommodation-filter-table__order__field'>
                    <OrderButton
                        name={t('contentSettingsPage.ACCOMMODATION_NAME')}
                        type={currentOrder}
                        active={currentOrderType === IOrderFields.Name}
                        activate={() => changeOrderType(IOrderFields.Name)}
                        changeOrder={() => changeOrder()}
                        sortable
                    />
                </div>
                <div className='accommodation-filter-table__order__field last'>
                    <OrderButton
                        name={t('form.LOCATION')}
                        type={currentOrder}
                        active={currentOrderType === IOrderFields.Location}
                        activate={() => changeOrderType(IOrderFields.Location)}
                        changeOrder={() => changeOrder()}
                        sortable
                    />
                </div>
            </div>
            {!loading && itemsPagination && itemsPagination.length > 0 && (
                <div className='accommodation-filter-table__items-list'>
                    {itemsPagination[currentPageNumber - 1].map(
                        (item: IAccommodationListItemDTO, index: number) => {
                            return (
                                <AccommodationFilterTableItem
                                    item={item}
                                    key={index}
                                    onDelete={() => {
                                        // remove item
                                        setConfirmationModalVisibility(true);
                                        setSelectedItem(item);
                                    }}
                                    onEdit={() => {
                                        // open modal to edit
                                        setCurrentMode(
                                            AccommodationModalMode.Edit,
                                        );
                                        setAccommodationModalVisibility(true);
                                        setSelectedItem(item);
                                    }}
                                />
                            );
                        },
                    )}
                </div>
            )}
            {loading && (
                <div className='accommodation-filter-table__loading'>
                    <Spin />
                </div>
            )}
            <div className='accommodation-filter-table__footer'>
                <div className='accommodation-filter-table__footer__left-column'>
                    <p>{t('general.SHOW')}</p>
                    <Dropdown
                        items={itemsPaginationDropdown}
                        value={`${totalPageNumber}`}
                        onChange={(e: string) => {
                            setCurrentPageNumber(1);
                            setTotalPageNumber(Number(e));
                        }}
                        allowClear={false}
                        showSearch={false}
                        placeholder={t('form.TYPE_PLACEHOLDER')}
                        className='accommodation-filter-table__footer__left-column__dropdown'
                    />
                    <p>
                        {t('general.OF')} {totalNumberOfItems}{' '}
                        {t('general.RESULTS')}
                    </p>
                </div>
                <div className='accommodation-filter-table__footer__right-column'>
                    <p>
                        {t('general.PAGE')} {currentPageNumber}{' '}
                        {t('general.OF')} {itemsPagination.length || 1}
                    </p>
                    <button
                        type='button'
                        className='accommodation-filter-table__footer__right-column__center-button'
                        onClick={() => {
                            if (currentPageNumber === 1) return;
                            setCurrentPageNumber(currentPageNumber - 1);
                        }}
                    >
                        <img src={ArrowIcon} alt='Arrow Icon' />
                    </button>
                    <button
                        type='button'
                        onClick={() => {
                            if (
                                currentPageNumber + 1 ===
                                itemsPagination.length + 1
                            )
                                return;
                            setCurrentPageNumber(currentPageNumber + 1);
                        }}
                    >
                        <img src={ArrowIcon} alt='Arrow Icon' />
                    </button>
                </div>
            </div>
        </div>
    );
}

export default AccommodationFilterTable;
